import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { companyInfo, usefulLinks } from '../data';
import SocialLinks from './SocialLinks';
import logo from "../assets/images/logo.png";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  // Safe fallbacks
  const info = companyInfo || {};
  const links = usefulLinks || [];

  return (
    <footer className="bg-secondary text-white">
      <div className="container-custom section-padding">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info with Logo and Name */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center space-x-3 mb-4">
              <img 
                src={logo} 
                alt={info.name || 'Company Logo'}
                className="h-14 w-auto brightness-0 invert filter" // Makes logo white
              />
              <div className="flex flex-col">
                <span className="font-oswald font-bold text-l leading-tight text-white">
                  {info.shortName || 'Al Abeer Diamond'}
                </span>
                <span className="text-xs font-roboto tracking-[0.15em] uppercase text-gray-400">
                  {info.tagline || 'Building Excellence, Ensuring Safety'}
                </span>
              </div>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mt-3">
              {info.description || 'Leading multi-disciplinary company in Saudi Arabia.'}
            </p>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h4 className="font-oswald text-lg font-semibold mb-4 text-white">Quick Links</h4>
            <ul className="space-y-2">
              {links.map((link) => (
                <li key={link.id}>
                  <Link 
                    to={link.url}
                    className="text-gray-400 hover:text-primary transition-colors text-sm"
                  >
                    {link.title}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h4 className="font-oswald text-lg font-semibold mb-4 text-white">Contact Us</h4>
            <div className="space-y-3 text-sm">
              <div className="flex items-start space-x-3">
                <i className="uil uil-location-pin-alt text-primary mt-1"></i>
                <span className="text-gray-400">{info.address || 'Jeddah, KSA'}</span>
              </div>
              <div className="flex items-center space-x-3">
                <i className="uil uil-envelope text-primary"></i>
                <a href={`mailto:${info.email || 'info@company.com'}`} className="text-gray-400 hover:text-primary transition-colors">
                  {info.email || 'info@company.com'}
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <i className="uil uil-phone-volume text-primary"></i>
                <div className="flex flex-col">
                  <a href={`tel:${(info.phone1 || '+966 53 522 1070').replace(/\s/g, '')}`} className="text-gray-400 hover:text-primary transition-colors">
                    {info.phone1 || '+966 53 522 1070'}
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Map */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <h4 className="font-oswald text-lg font-semibold mb-4 text-white">Location</h4>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3710.123456789012!2d39.172123!3d21.543212!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x15c3d1234567890b%3A0x1234567890abcdef!2sOld%20Makkah%20Road%2C%20Al%20Mursalat%2C%20Jeddah%20Saudi%20Arabia!5e0!3m2!1sen!2sus!4v1748419750000!5m2!1sen!2sus"
              width="100%"
              height="200"
              style={{ border: 0, borderRadius: '8px' }}
              allowFullScreen=""
              loading="lazy"
              title="Company Location"
              className="rounded-lg"
            ></iframe>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <p className="text-gray-400 text-sm">
            &copy; {currentYear} {info.name || 'Al Abeer Diamond Company LLC'}. All rights reserved.
          </p>
          <div className="flex items-center space-x-4">
            <SocialLinks className="flex space-x-2" />
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;